<template>
  <div class="challenge-entry">
    <MainNavbar />

    <div class="challenge-team-page challenge-team--basketball">
      <!-- Page hero -->
      <section class="page-hero">
        <div class="page-hero-inner">
          <p class="page-kicker">Match Hub · Basketball</p>
          <h1 class="page-title">
            <i class="fa-solid fa-basketball" aria-hidden="true"></i>
            Request Game
          </h1>
          <p class="page-lead">
            Organize a basketball match with
            <strong>{{ opponentTeam?.name || "your selected opponent" }}</strong>
          </p>
        </div>
      </section>

      <!-- Loading -->
      <div v-if="loading" class="state-card loading-card">
        <div class="state-icon">
          <i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
        </div>
        <h3>Loading team information</h3>
        <p>Fetching your squad and opponent details…</p>
      </div>

      <!-- Main content -->
      <div v-else-if="myTeam && opponentTeam" class="content-wrap">
        <!-- Teams matchup -->
        <section class="panel teams-panel" aria-label="Teams matchup">
          <div class="panel-head">
            <h2><i class="fa-solid fa-users" aria-hidden="true"></i> Teams</h2>
          </div>
          <div class="teams-row">
            <article class="team-card home-team">
              <div class="team-logo">
                <img
                  v-if="myTeam.logo_url"
                  :src="myTeam.logo_url"
                  :alt="myTeam.name"
                  decoding="async"
                  @error="handleImageError"
                />
                <div v-else class="logo-placeholder">
                  <i class="fa-solid fa-basketball" aria-hidden="true"></i>
                </div>
              </div>
              <span class="team-badge">Your team</span>
              <h3>{{ myTeam.name }}</h3>
              <p class="team-meta">
                <i class="fa-solid fa-map-marker-alt" aria-hidden="true"></i>
                {{ myTeam.county }}{{ myTeam.constituency ? `, ${myTeam.constituency}` : "" }}
              </p>
              <p v-if="myTeam.home_ground" class="team-ground">
                <i class="fa-solid fa-home" aria-hidden="true"></i>
                {{ myTeam.home_ground }}
              </p>
            </article>

            <div class="vs-pill" aria-hidden="true">VS</div>

            <article class="team-card away-team">
              <div class="team-logo">
                <img
                  v-if="opponentTeam.logo_url"
                  :src="opponentTeam.logo_url"
                  :alt="opponentTeam.name"
                  decoding="async"
                  @error="handleImageError"
                />
                <div v-else class="logo-placeholder">
                  <i class="fa-solid fa-basketball" aria-hidden="true"></i>
                </div>
              </div>
              <span class="team-badge opponent">Opponent</span>
              <h3>{{ opponentTeam.name }}</h3>
              <p class="team-meta">
                <i class="fa-solid fa-map-marker-alt" aria-hidden="true"></i>
                {{ opponentTeam.county }}{{ opponentTeam.constituency ? `, ${opponentTeam.constituency}` : "" }}
              </p>
              <p v-if="opponentTeam.home_ground" class="team-ground">
                <i class="fa-solid fa-home" aria-hidden="true"></i>
                {{ opponentTeam.home_ground }}
              </p>
            </article>
          </div>
        </section>

        <!-- Opponent profile (from GET /api/teams/basketball/{team_id}) -->
        <section class="panel profile-panel" aria-label="Opponent team profile">
          <div class="panel-head">
            <h2><i class="fa-solid fa-user-circle" aria-hidden="true"></i> Opponent profile</h2>
            <p>Details loaded for the team you selected on Organize Match.</p>
          </div>
          <div class="profile-grid">
            <div class="profile-card">
              <h3><i class="fa-solid fa-phone" aria-hidden="true"></i> Contact</h3>
              <div v-if="opponentTeam.phone" class="profile-row">
                <span class="profile-label">Phone</span>
                <a class="profile-value link" :href="`tel:${opponentTeam.phone}`">{{ opponentTeam.phone }}</a>
              </div>
              <p v-else class="profile-muted">Phone not provided</p>
              <div v-if="opponentTeam.owner_name" class="profile-row">
                <span class="profile-label">Owner</span>
                <span class="profile-value">{{ opponentTeam.owner_name }}</span>
              </div>
            </div>
            <div class="profile-card">
              <h3><i class="fa-solid fa-map-marker-alt" aria-hidden="true"></i> Location</h3>
              <div v-if="opponentTeam.home_ground" class="profile-row">
                <span class="profile-label">Home court</span>
                <span class="profile-value">{{ opponentTeam.home_ground }}</span>
              </div>
              <div v-if="opponentTeam.county" class="profile-row">
                <span class="profile-label">County</span>
                <span class="profile-value">{{ opponentTeam.county }}</span>
              </div>
              <div v-if="opponentTeam.constituency" class="profile-row">
                <span class="profile-label">Constituency</span>
                <span class="profile-value">{{ opponentTeam.constituency }}</span>
              </div>
            </div>
            <div class="profile-card">
              <h3><i class="fa-solid fa-basketball" aria-hidden="true"></i> Squad info</h3>
              <div v-if="opponentTeam.game_format" class="profile-row">
                <span class="profile-label">Format</span>
                <span class="profile-value">{{ formatGameFormat(opponentTeam.game_format) }}</span>
              </div>
              <div v-if="opponentTeam.court_type" class="profile-row">
                <span class="profile-label">Court</span>
                <span class="profile-value">{{ formatCourtType(opponentTeam.court_type) }}</span>
              </div>
              <div v-if="opponentTeam.skill_level" class="profile-row">
                <span class="profile-label">Skill</span>
                <span class="profile-value">{{ formatSkillLevel(opponentTeam.skill_level) }}</span>
              </div>
              <div v-if="opponentTeam.member_count != null" class="profile-row">
                <span class="profile-label">Roster</span>
                <span class="profile-value">{{ opponentTeam.member_count }} active</span>
              </div>
            </div>
          </div>
          <p v-if="opponentTeam.bio" class="profile-bio">{{ opponentTeam.bio }}</p>
        </section>

        <!-- Match form -->
        <section class="panel form-panel">
          <div class="panel-head">
            <h2><i class="fa-solid fa-calendar-check" aria-hidden="true"></i> Match details</h2>
            <p>Fill in the schedule, format, and venue for this challenge.</p>
          </div>

          <form class="match-form" @submit.prevent="submitMatch">
            <div class="form-section">
              <h3 class="form-section-title">Schedule</h3>
              <div class="field">
                <label for="match-date">Match date <span class="req">*</span></label>
                <input
                  id="match-date"
                  v-model="matchForm.match_date"
                  type="date"
                  :min="minDate"
                  required
                />
                <span v-if="errors.match_date" class="field-error">{{ errors.match_date }}</span>
              </div>

              <div class="field-row">
                <div class="field">
                  <label for="kickoff-time">Tip-off time <span class="req">*</span></label>
                  <input
                    id="kickoff-time"
                    v-model="matchForm.kickoff_time"
                    type="time"
                    required
                  />
                  <span v-if="errors.kickoff_time" class="field-error">{{ errors.kickoff_time }}</span>
                </div>
                <div class="field">
                  <label for="duration">Duration <span class="req">*</span></label>
                  <select id="duration" v-model="matchForm.duration" required>
                    <option value="">Select duration</option>
                    <option v-for="opt in durationOptions" :key="opt.value" :value="opt.value">
                      {{ opt.label }}
                    </option>
                  </select>
                  <span v-if="errors.duration" class="field-error">{{ errors.duration }}</span>
                </div>
              </div>
            </div>

            <div class="form-section">
              <h3 class="form-section-title">Format</h3>
              <div class="field-row">
                <div class="field">
                  <label for="match-format">Game format <span class="req">*</span></label>
                  <select id="match-format" v-model="matchForm.match_format" required>
                    <option value="">Select format</option>
                    <option v-for="opt in matchFormatOptions" :key="opt.value" :value="opt.value">
                      {{ opt.label }}
                    </option>
                  </select>
                  <span v-if="errors.match_format" class="field-error">{{ errors.match_format }}</span>
                </div>
                <div class="field">
                  <label for="match-type">Match type <span class="req">*</span></label>
                  <select id="match-type" v-model="matchForm.match_type" required>
                    <option value="">Select type</option>
                    <option value="friendly">Friendly</option>
                    <option value="competitive">Competitive</option>
                    <option value="tournament">Tournament</option>
                    <option value="league">League</option>
                  </select>
                  <span v-if="errors.match_type" class="field-error">{{ errors.match_type }}</span>
                </div>
              </div>
            </div>

            <div class="form-section">
              <h3 class="form-section-title">Venue</h3>
              <div class="field">
                <span class="field-label">Where will you play? <span class="req">*</span></span>
                <div
                  class="venue-chips"
                  role="radiogroup"
                  aria-label="Venue selection"
                  :aria-disabled="isNeutralVenueLocked ? 'true' : 'false'"
                >
                  <label
                    class="venue-chip"
                    :class="{
                      selected: matchForm.venue_type === 'opponent',
                      'venue-chip--disabled': isNeutralVenueLocked,
                    }"
                  >
                    <input
                      v-model="matchForm.venue_type"
                      type="radio"
                      value="opponent"
                      :disabled="isNeutralVenueLocked"
                      @change="onVenueTypeChange"
                    />
                    <i class="fa-solid fa-plane-arrival" aria-hidden="true"></i>
                    <span>Opponent home</span>
                  </label>
                  <label
                    class="venue-chip"
                    :class="{
                      selected: matchForm.venue_type === 'challenger',
                      'venue-chip--disabled': isNeutralVenueLocked,
                    }"
                  >
                    <input
                      v-model="matchForm.venue_type"
                      type="radio"
                      value="challenger"
                      :disabled="isNeutralVenueLocked"
                      @change="onVenueTypeChange"
                    />
                    <i class="fa-solid fa-house" aria-hidden="true"></i>
                    <span>Your home</span>
                  </label>
                  <label
                    class="venue-chip"
                    :class="{
                      selected: matchForm.venue_type === 'neutral',
                      'venue-chip--disabled': isNeutralVenueLocked,
                    }"
                  >
                    <input
                      v-model="matchForm.venue_type"
                      type="radio"
                      value="neutral"
                      :disabled="isNeutralVenueLocked"
                      @change="onVenueTypeChange"
                    />
                    <i class="fa-solid fa-scale-balanced" aria-hidden="true"></i>
                    <span>Neutral court</span>
                  </label>
                </div>
                <p v-if="isNeutralVenueLocked" class="venue-locked-hint">
                  Home and away options are locked while your neutral court booking is active.
                </p>
                <span v-if="errors.venue_type" class="field-error">{{ errors.venue_type }}</span>
              </div>

              <div v-if="matchForm.venue_type === 'neutral'" class="neutral-picker-wrap">
                <NeutralCourtPicker
                  :facility-type="MATCH_SPORT"
                  :booked="neutralCourtBooking"
                  :locked="isNeutralVenueLocked"
                  :auto-open-browse="neutralAutoOpenBrowse"
                  :suggested-date="matchForm.match_date"
                  :suggested-start-time="matchForm.kickoff_time"
                  :suggested-duration-minutes="neutralBookingDurationMinutes"
                  :challenger-team-id="myTeam?.team_id"
                  :opponent-team-id="opponentTeam?.team_id"
                  @update:booked="onNeutralCourtBooked"
                  @cleared="onNeutralCourtCleared"
                  @browse-closed="neutralAutoOpenBrowse = false"
                />
                <span v-if="errors.neutral_venue_name" class="field-error">{{ errors.neutral_venue_name }}</span>
              </div>
            </div>

            <div class="form-section officiating-section">
              <h3 class="form-section-title">
                <i class="fa-solid fa-user-check" aria-hidden="true"></i>
                Game officiating
              </h3>
              <p class="form-section-lead">
                Need registered refs or table crew? Officials are notified after your opponent confirms.
              </p>

              <div class="field">
                <span class="field-label">Do you need game officiating?</span>
                <div
                  class="officiating-toggle"
                  role="radiogroup"
                  aria-label="Game officiating preference"
                >
                  <label
                    class="officiating-toggle__option"
                    :class="{ selected: matchForm.needs_officiating === false }"
                  >
                    <input
                      v-model="matchForm.needs_officiating"
                      type="radio"
                      :value="false"
                      @change="onOfficiatingPreferenceChange"
                    />
                    <i class="fa-solid fa-ban" aria-hidden="true"></i>
                    <span>No thanks</span>
                  </label>
                  <label
                    class="officiating-toggle__option"
                    :class="{ selected: matchForm.needs_officiating === true }"
                  >
                    <input
                      v-model="matchForm.needs_officiating"
                      type="radio"
                      :value="true"
                      @change="onOfficiatingPreferenceChange"
                    />
                    <i class="fa-solid fa-user-shield" aria-hidden="true"></i>
                    <span>Yes, book officials</span>
                  </label>
                </div>
              </div>

              <transition name="officiating-reveal">
                <div v-if="matchForm.needs_officiating" class="officiating-details">
                  <p v-if="matchForm.match_format === '3v3'" class="officiating-format-hint">
                    <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
                    3v3 games typically use one lead referee.
                  </p>
                  <p v-else-if="matchForm.match_format === '5v5'" class="officiating-format-hint">
                    <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
                    5v5 games usually need two on-court refs; add a table official for full crew.
                  </p>

                  <div class="field">
                    <span class="field-label">
                      How many officials do you need? <span class="req">*</span>
                    </span>
                    <div class="officiating-count-grid" role="radiogroup" aria-label="Official count">
                      <label
                        v-for="opt in officiatingCountOptions"
                        :key="opt.count"
                        class="officiating-count-card"
                        :class="{ selected: matchForm.officiating_count === opt.count }"
                      >
                        <input
                          v-model.number="matchForm.officiating_count"
                          type="radio"
                          :value="opt.count"
                          @change="clearOfficiatingError"
                        />
                        <span class="officiating-count-card__icon" aria-hidden="true">
                          <i :class="opt.icon"></i>
                        </span>
                        <span class="officiating-count-card__label">{{ opt.label }}</span>
                        <span class="officiating-count-card__summary">{{ opt.summary }}</span>
                        <span class="officiating-count-card__fee">
                          from {{ formatKes(officiatingMinimumCostKes(opt.count)) }}
                        </span>
                      </label>
                    </div>
                    <span v-if="errors.officiating_count" class="field-error">
                      {{ errors.officiating_count }}
                    </span>
                  </div>

                  <div
                    v-if="matchForm.officiating_count"
                    class="officiating-slots-panel"
                    aria-live="polite"
                  >
                    <div class="officiating-slots-panel__head">
                      <h4>
                        <i class="fa-solid fa-list-check" aria-hidden="true"></i>
                        Your officiating crew
                      </h4>
                      <p>Minimum hiring charges per slot — final fees may vary by official.</p>
                    </div>

                    <ul class="officiating-slot-list">
                      <li
                        v-for="slot in activeOfficiatingSlots"
                        :key="slot.slot"
                        class="officiating-slot-row"
                      >
                        <span class="officiating-slot-row__icon" aria-hidden="true">
                          <i :class="slot.icon"></i>
                        </span>
                        <div class="officiating-slot-row__body">
                          <strong>{{ slot.label }}</strong>
                          <span>{{ slot.role }}</span>
                        </div>
                        <span class="officiating-slot-row__fee">
                          min {{ formatKes(officiatingSlotMinimumKes(slot.slot)) }}
                        </span>
                      </li>
                    </ul>

                    <div class="officiating-total">
                      <span>
                        <i class="fa-solid fa-coins" aria-hidden="true"></i>
                        Estimated minimum total
                      </span>
                      <strong>{{ formatKes(officiatingTotalMinimumKes) }}</strong>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <div class="form-section">
              <h3 class="form-section-title">Notes</h3>
              <div class="field">
                <label for="description">Description <span class="optional">(optional)</span></label>
                <textarea
                  id="description"
                  v-model="matchForm.description"
                  rows="4"
                  maxlength="500"
                  placeholder="Add any extra details for the opponent…"
                />
              </div>
            </div>

            <div class="form-actions">
              <button type="button" class="btn outline" @click="goBack">
                <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>
                Cancel
              </button>
              <button type="submit" class="btn primary" :disabled="isSubmitting">
                <i v-if="!isSubmitting" class="fa-solid fa-check-circle" aria-hidden="true"></i>
                <i v-else class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                {{ isSubmitting ? "Submitting…" : "Review & submit" }}
              </button>
            </div>
          </form>
        </section>
      </div>

      <!-- Confirmation modal -->
      <transition name="modal-fade">
        <div
          v-if="showConfirmationModal"
          class="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
          @click="closeConfirmationModal"
        >
          <div class="modal-card" @click.stop>
            <div class="modal-icon">
              <i class="fa-solid fa-clipboard-check" aria-hidden="true"></i>
            </div>
            <h3 id="confirm-title">Ready to submit?</h3>
            <p class="modal-lead">Please confirm all match details are correct before sending the challenge.</p>
            <div
              v-if="matchForm.needs_officiating && matchForm.officiating_count"
              class="modal-officiating-note modal-officiating-note--active"
              role="note"
            >
              <p class="modal-officiating-note__title">
                <i class="fa-solid fa-user-shield" aria-hidden="true"></i>
                {{ matchForm.officiating_count }} official{{ matchForm.officiating_count > 1 ? "s" : "" }} requested
              </p>
              <ul class="modal-officiating-slots">
                <li v-for="slot in activeOfficiatingSlots" :key="slot.slot">
                  <i :class="slot.icon" aria-hidden="true"></i>
                  <span>{{ slot.label }} — {{ slot.role }}</span>
                  <em>min {{ formatKes(officiatingSlotMinimumKes(slot.slot)) }}</em>
                </li>
              </ul>
              <p class="modal-officiating-note__fee">
                Estimated minimum total: <strong>{{ formatKes(officiatingTotalMinimumKes) }}</strong>.
                Officials are only involved after your opponent confirms their availability and accepts
                this challenge. The match is then listed in the officiating pool, where registered
                officials browse open opportunities and apply to officiate — a way for them to earn
                from qualified matches. You do not pick or assign officials yourself.
              </p>
              <p class="modal-officiating-note__split">
                <i class="fa-solid fa-handshake" aria-hidden="true"></i>
                You may split payment with your opponent after the game — recommended, not required.
              </p>
            </div>
            <div v-else class="modal-officiating-note" role="note">
              <p class="modal-officiating-note__title">
                <i class="fa-solid fa-user-check" aria-hidden="true"></i>
                No officials requested
              </p>
              <p>
                This game will proceed without registered officials. You can still agree on informal
                officiating with the opponent on the day.
              </p>
            </div>
            <div class="modal-actions">
              <button type="button" class="btn outline" @click="closeConfirmationModal">Back</button>
              <button
                type="button"
                class="btn primary"
                :disabled="isSubmitting"
                @click="confirmAndSubmitMatch"
              >
                {{ isSubmitting ? "Submitting…" : "Submit match" }}
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Error state -->
      <div v-if="error && !loading" class="state-card error-card">
        <div class="state-icon error">
          <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
        </div>
        <h3>Something went wrong</h3>
        <p>{{ error }}</p>
        <button type="button" class="btn primary" @click="goBack">
          <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>
          Go back
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import NeutralCourtPicker from "@/routes/match_hub/shared/NeutralCourtPicker.vue";
import { buildApiUrl } from "@/config/api";
import {
  facilityAcceptsBookingForSport,
  resolvePitchTypeForSport,
} from "@/utils/facilityTypes";

import {
  activeOfficiatingSlots,
  defaultOfficiatingCountForFormat,
  formatKes,
  officiatingCountOptionsForFormat,
  officiatingMinimumCostKes,
  officiatingSlotMinimumKes,
} from "@/routes/match_hub/basketball/officiatingConstants.js";

const MATCH_SPORT = "basketball";

export default {
  name: "BasketballChallengeTeam",
  components: { MainNavbar, NeutralCourtPicker },
  data() {
    return {
      MATCH_SPORT,
      formatKes,
      officiatingMinimumCostKes,
      officiatingSlotMinimumKes,
      loading: true,
      error: null,
      myTeam: null,
      opponentTeam: null,
      durationOptions: [
        { value: "40", label: "40 minutes (4×10)" },
        { value: "48", label: "48 minutes (4×12)" },
        { value: "60", label: "60 minutes (running clock)" },
      ],
      matchFormatOptions: [
        { value: "5v5", label: "5v5 full court" },
        { value: "3v3", label: "3v3 half court" },
      ],
      maxPlayersMap: {
        "5v5": 5,
        "3v3": 3,
      },
      matchForm: {
        match_date: "",
        kickoff_time: "",
        duration: "",
        match_format: "",
        match_type: "friendly",
        venue_type: "",
        neutral_venue_name: "",
        neutral_venue_pin: "",
        description: "",
        needs_officiating: false,
        officiating_count: null,
      },
      showConfirmationModal: false,
      errors: {},
      isSubmitting: false,
      neutralCourtBooking: null,
      neutralAutoOpenBrowse: false,
    };
  },
  computed: {
    minDate() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today.toISOString().split("T")[0];
    },
    opponentTeamId() {
      const raw = this.$route.params.team_id;
      const id = Number(raw);
      return Number.isFinite(id) && id > 0 ? id : null;
    },
    isNeutralVenueLocked() {
      return Boolean(this.neutralCourtBooking?.court?.pitch_name);
    },
    neutralBookingDurationMinutes() {
      const matchMinutes = parseInt(this.matchForm.duration, 10);
      const minutes = Number.isFinite(matchMinutes) && matchMinutes > 0 ? matchMinutes : 60;
      return Math.max(minutes, 60);
    },
    officiatingCountOptions() {
      return officiatingCountOptionsForFormat(this.matchForm.match_format);
    },
    activeOfficiatingSlots() {
      return activeOfficiatingSlots(this.matchForm.officiating_count);
    },
    officiatingTotalMinimumKes() {
      return officiatingMinimumCostKes(this.matchForm.officiating_count);
    },
  },
  watch: {
    opponentTeamId: {
      immediate: false,
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.loadTeams();
        }
      },
    },
    "matchForm.match_format"(format) {
      if (!this.matchForm.needs_officiating) return;
      const allowed = officiatingCountOptionsForFormat(format).map((o) => o.count);
      const current = this.matchForm.officiating_count;
      if (!allowed.includes(current)) {
        this.matchForm.officiating_count = defaultOfficiatingCountForFormat(format);
      }
    },
  },
  async mounted() {
    await this.loadTeams();
  },
  methods: {
    getAuthToken() {
      return (
        localStorage.getItem("access_token") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("access_token") ||
        sessionStorage.getItem("token")
      );
    },
    parseApiError(body, fallback) {
      const detail = body?.detail;
      if (typeof detail === "string") return detail;
      if (detail?.message) return detail.message;
      if (Array.isArray(detail)) {
        return detail.map((d) => d.msg || d.message || String(d)).filter(Boolean).join(" ");
      }
      return body?.message || fallback;
    },

    normalizeTeamRecord(raw) {
      if (!raw || typeof raw !== "object") return null;
      const teamId = raw.team_id ?? raw.team_id_num;
      if (teamId == null) return null;
      return {
        team_id: Number(teamId),
        name: raw.name ?? raw.team_name ?? "",
        team_type: raw.team_type || "",
        logo_url: raw.logo_url || null,
        phone: raw.phone || null,
        bio: raw.bio || "",
        county: raw.county || "",
        constituency: raw.constituency || "",
        home_ground: raw.home_ground || "",
        home_ground_pin_location: raw.home_ground_pin_location || null,
        game_format: raw.game_format || "",
        court_type: raw.court_type || "",
        game_structure: raw.game_structure || "",
        skill_level: raw.skill_level || "",
        member_count: raw.member_count ?? null,
        owner_name: raw.owner_name || null,
        is_verified: Boolean(raw.is_verified),
      };
    },

    applyOpponentPreview(preview) {
      if (!preview || typeof preview !== "object") return;
      const normalized = this.normalizeTeamRecord(preview);
      if (normalized) this.opponentTeam = normalized;
    },

    async loadTeams() {
      if (!this.opponentTeamId) {
        this.error = "Invalid opponent team. Go back and select a team from Organize Match.";
        this.loading = false;
        return;
      }

      this.loading = true;
      this.error = null;
      this.myTeam = null;

      const routePreview =
        typeof history !== "undefined" ? history.state?.opponentPreview : null;
      if (routePreview && Number(routePreview.team_id) === this.opponentTeamId) {
        this.applyOpponentPreview(routePreview);
      } else {
        this.opponentTeam = null;
      }

      try {
        const token = this.getAuthToken();
        if (!token) throw new Error("Authentication required. Please sign in.");

        const headers = { Authorization: `Bearer ${token}` };
        const [myRes, oppRes] = await Promise.all([
          fetch(buildApiUrl("/api/teams/basketball/my-team"), { headers }),
          fetch(buildApiUrl(`/api/teams/basketball/${this.opponentTeamId}`), { headers }),
        ]);

        if (!myRes.ok) {
          const body = await myRes.json().catch(() => ({}));
          throw new Error(
            this.parseApiError(
              body,
              myRes.status === 404
                ? "You need a basketball team before requesting a game. Register your team first."
                : "Failed to load your basketball team.",
            ),
          );
        }

        if (!oppRes.ok) {
          const body = await oppRes.json().catch(() => ({}));
          throw new Error(
            this.parseApiError(
              body,
              oppRes.status === 404
                ? "Opponent team not found. They may have been removed from the hub."
                : "Failed to load opponent profile.",
            ),
          );
        }

        const myPayload = await myRes.json();
        const oppPayload = await oppRes.json();

        const myRaw = Array.isArray(myPayload?.teams) ? myPayload.teams[0] : myPayload?.team || myPayload;
        const oppRaw = Array.isArray(oppPayload?.teams) ? oppPayload.teams[0] : oppPayload?.team || oppPayload;

        const myTeam = this.normalizeTeamRecord(myRaw);
        const opponentTeam = this.normalizeTeamRecord(oppRaw);

        if (!myTeam) throw new Error("Could not read your team profile from the server.");
        if (!opponentTeam) throw new Error("Could not read opponent profile from the server.");

        if (myTeam.team_id === opponentTeam.team_id) {
          throw new Error("You cannot request a game against your own team.");
        }

        this.myTeam = myTeam;
        this.opponentTeam = opponentTeam;
      } catch (e) {
        this.error = e.message || "Failed to load teams";
        if (!this.opponentTeam && routePreview && Number(routePreview.team_id) === this.opponentTeamId) {
          this.applyOpponentPreview(routePreview);
        }
      } finally {
        this.loading = false;
      }
    },

    handleImageError(event) {
      if (event?.target) event.target.style.display = "none";
    },

    formatGameFormat(value) {
      const map = { "5v5": "5v5 full court", "3v3": "3v3 half court", both: "5v5 & 3v3" };
      return map[value] || value;
    },

    formatCourtType(value) {
      const map = { indoor: "Indoor", outdoor: "Outdoor", both: "Indoor & outdoor" };
      return map[value] || value;
    },

    formatSkillLevel(value) {
      if (!value) return "";
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    onVenueTypeChange() {
      if (this.isNeutralVenueLocked) {
        this.matchForm.venue_type = "neutral";
        return;
      }
      if (this.matchForm.venue_type === "neutral") {
        this.neutralAutoOpenBrowse = !this.neutralCourtBooking;
      } else {
        this.neutralAutoOpenBrowse = false;
        this.onNeutralCourtCleared();
      }
      delete this.errors.venue_type;
      delete this.errors.neutral_venue_name;
    },
    onNeutralCourtBooked(payload) {
      const court = payload?.court;
      if (court && !facilityAcceptsBookingForSport(court.pitch_type, MATCH_SPORT)) {
        this.errors.neutral_venue_name =
          "Selected facility is not a basketball court. Choose a basketball listing.";
        this.onNeutralCourtCleared();
        return;
      }
      this.neutralCourtBooking = payload;
      this.matchForm.venue_type = "neutral";
      this.neutralAutoOpenBrowse = false;
      this.matchForm.neutral_venue_name = court?.pitch_name || "";
      this.matchForm.neutral_venue_pin = court?.pin_location || "";
      delete this.errors.neutral_venue_name;
      delete this.errors.venue_type;
    },
    onNeutralCourtCleared() {
      this.neutralCourtBooking = null;
      this.matchForm.neutral_venue_name = "";
      this.matchForm.neutral_venue_pin = "";
    },

    onOfficiatingPreferenceChange() {
      if (this.matchForm.needs_officiating) {
        if (!this.matchForm.officiating_count) {
          this.matchForm.officiating_count = defaultOfficiatingCountForFormat(
            this.matchForm.match_format,
          );
        }
      } else {
        this.matchForm.officiating_count = null;
      }
      this.clearOfficiatingError();
    },

    clearOfficiatingError() {
      delete this.errors.officiating_count;
    },

    submitMatch() {
      this.errors = {};
      if (!this.matchForm.match_date) this.errors.match_date = "Match date is required";
      if (!this.matchForm.kickoff_time) this.errors.kickoff_time = "Tip-off time is required";
      if (!this.matchForm.duration) this.errors.duration = "Duration is required";
      if (!this.matchForm.match_format) this.errors.match_format = "Game format is required";
      if (!this.matchForm.match_type) this.errors.match_type = "Match type is required";
      if (!this.matchForm.venue_type) this.errors.venue_type = "Please select a venue option";
      if (this.matchForm.venue_type === "neutral") {
        const court = this.neutralCourtBooking?.court;
        if (!court?.pitch_name) {
          this.errors.neutral_venue_name = "Book a neutral court before submitting.";
        } else if (!facilityAcceptsBookingForSport(court.pitch_type, MATCH_SPORT)) {
          this.errors.neutral_venue_name =
            "Neutral venue must be a basketball court (see facility type on listing).";
        }
      }
      if (this.matchForm.needs_officiating && !this.matchForm.officiating_count) {
        this.errors.officiating_count = "Select how many officials you need.";
      }
      if (Object.keys(this.errors).length) return;
      this.showConfirmationModal = true;
    },
    closeConfirmationModal() {
      this.showConfirmationModal = false;
    },
    async confirmAndSubmitMatch() {
      this.isSubmitting = true;
      try {
        const token = this.getAuthToken();
        if (!token) throw new Error("Authentication required");

        const matchDateTime = `${this.matchForm.match_date}T${this.matchForm.kickoff_time}:00`;
        const maxPlayers = this.maxPlayersMap[this.matchForm.match_format];
        if (!maxPlayers) {
          throw new Error("Select a valid game format (5v5 or 3v3).");
        }

        if (this.matchForm.venue_type === "neutral") {
          const court = this.neutralCourtBooking?.court;
          if (!court?.pitch_name) {
            throw new Error("Book a neutral basketball court before submitting.");
          }
          if (!facilityAcceptsBookingForSport(court.pitch_type, MATCH_SPORT)) {
            throw new Error(
              "Neutral venue must be a basketball court. Re-book from the basketball court list.",
            );
          }
        }

        let venueName = "";
        let venuePin = null;
        let venueCity = "";

        if (this.matchForm.venue_type === "opponent") {
          venueName = this.opponentTeam.home_ground || "Opponent Home";
          venuePin = this.opponentTeam.home_ground_pin_location || null;
          venueCity = this.opponentTeam.county || "Kenya";
        } else if (this.matchForm.venue_type === "challenger") {
          venueName = this.myTeam.home_ground || "Your Home";
          venuePin = this.myTeam.home_ground_pin_location || null;
          venueCity = this.myTeam.county || "Kenya";
        } else {
          const court = this.neutralCourtBooking?.court;
          venueName = court?.pitch_name || this.matchForm.neutral_venue_name || "Neutral Court";
          venuePin = court?.pin_location || this.matchForm.neutral_venue_pin || null;
          venueCity =
            court?.resident_county ||
            this.opponentTeam.county ||
            this.myTeam.county ||
            "Kenya";
        }

        if (!venueName?.trim()) venueName = "TBD";
        if (!venueCity?.trim()) venueCity = "Kenya";

        const payload = {
          team_id: this.myTeam.team_id,
          opponent_team_id: this.opponentTeam.team_id,
          opponent_name: this.opponentTeam.name,
          match_date: matchDateTime,
          duration: parseInt(this.matchForm.duration, 10),
          match_type: this.matchForm.match_type,
          venue: {
            name: venueName,
            city: venueCity,
            address: this.matchForm.venue_type === "neutral" ? this.neutralCourtBooking?.court?.address || null : null,
            notes: venuePin,
            booking_details:
              this.matchForm.venue_type === "neutral" && this.neutralCourtBooking?.booking
                ? {
                    booking_number: this.neutralCourtBooking.booking.booking_number || null,
                    pitch_id: this.neutralCourtBooking.booking.pitch_id ?? this.neutralCourtBooking.court?.id ?? null,
                    pitch_name: this.neutralCourtBooking.court?.pitch_name || null,
                    pitch_type: resolvePitchTypeForSport(
                      this.neutralCourtBooking.court?.pitch_type,
                      MATCH_SPORT,
                    ),
                    pitch_address: this.neutralCourtBooking.court?.address || null,
                    pitch_zone: this.neutralCourtBooking.court?.zone || null,
                    pitch_county: this.neutralCourtBooking.court?.resident_county || null,
                    pitch_pin_location: venuePin || null,
                    owner_phone: this.neutralCourtBooking.court?.owner_phone || null,
                    booking_date: this.neutralCourtBooking.booking.date || this.matchForm.match_date,
                    start_time: this.neutralCourtBooking.booking.start_time || this.matchForm.kickoff_time,
                    end_time: this.neutralCourtBooking.booking.end_time || null,
                    total_cost: this.neutralCourtBooking.booking.total_cost || null,
                    booking_purpose: "neutral_ground",
                  }
                : null,
          },
          description: this.matchForm.description || null,
          max_players: maxPlayers,
          match_format: this.matchForm.match_format,
          notify_team: true,
          notify_opponent: true,
          needs_officiating: this.matchForm.needs_officiating,
          officiating_count: this.matchForm.needs_officiating
            ? this.matchForm.officiating_count
            : null,
        };

        const response = await fetch(
          buildApiUrl(`/api/matches/basketball/matches/create/${this.opponentTeamId}`),
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(
            this.parseApiError(err, `Match creation failed (${response.status})`),
          );
        }

        this.$router.push({ name: "BasketballTeamDashboard", query: { matchCreated: "true" } });
      } catch (e) {
        this.error = e.message || "Failed to create match";
        this.showConfirmationModal = false;
      } finally {
        this.isSubmitting = false;
      }
    },
    goBack() {
      this.$router.push({ name: "BasketballOrganizeMatch" });
    },
  },
};
</script>

<style scoped>
/* Aligned with MainEntry.vue: Poppins, CSS vars, bordered panels, accent chips */
.challenge-entry {
  font-family: "Poppins", sans-serif;
  color: var(--text-color1);
  background: var(--background-color2);
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
}

.challenge-team-page {
  --hub-gutter: clamp(16px, 4vw, 28px);
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 88px var(--hub-gutter) 48px;
  box-sizing: border-box;
}

.page-hero {
  margin-bottom: 28px;
  padding: clamp(24px, 4vw, 32px) clamp(20px, 3vw, 28px);
  border-radius: clamp(12px, 2vw, 16px);
  border: 1px solid var(--border-color-hover1);
  background: var(--background-color1);
  box-shadow: 0 4px 18px var(--box-shadow);
  position: relative;
  overflow: hidden;
}

.page-hero::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--border-color-hover);
  border-radius: clamp(12px, 2vw, 16px) 0 0 clamp(12px, 2vw, 16px);
}

.page-hero-inner {
  position: relative;
  text-align: left;
}

.page-kicker {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--border-color-hover);
  margin: 0 0 10px;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  font-size: clamp(1.5rem, 3.5vw, 2.2rem);
  font-weight: 800;
  margin: 0 0 12px;
  color: var(--text-color1);
}

.page-title i {
  color: var(--border-color-hover);
  font-size: 1.1em;
}

.page-lead {
  margin: 0;
  font-size: clamp(0.88rem, 2.2vw, 1rem);
  line-height: 1.55;
  color: var(--text-color2);
  max-width: none;
}

.page-lead strong {
  color: var(--border-color-hover);
  font-weight: 700;
}

/* Panels — feature-card / resale-col */
.content-wrap {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.panel {
  width: 100%;
  box-sizing: border-box;
  background: var(--background-color1);
  border: 1px solid var(--border-color-hover1);
  border-radius: 18px;
  padding: 24px clamp(18px, 2.5vw, 32px);
  box-shadow: 0 4px 18px var(--box-shadow);
  transition: box-shadow 0.25s ease;
}

.panel:hover {
  box-shadow: 0 8px 28px var(--box-shadow);
}

.panel-head {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px dashed var(--border-color-hover1);
}

.panel-head h2 {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0 0 6px;
  color: var(--text-color1);
}

.panel-head h2 i {
  color: var(--border-color-hover);
}

.panel-head p {
  margin: 0;
  font-size: 0.92rem;
  color: var(--text-color2);
  line-height: 1.45;
}

/* Teams */
.teams-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: clamp(16px, 2vw, 32px);
  align-items: stretch;
  width: 100%;
}

.team-card {
  background: var(--background-color2);
  border: 2px solid var(--border-color-hover1);
  border-radius: 16px;
  padding: 20px 18px;
  text-align: center;
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
}

.team-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 22px var(--box-shadow);
}

.team-card.home-team {
  border-color: var(--border-color-hover);
}

.team-card.away-team {
  border-color: var(--border-color-hover1);
}

.team-badge {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 999px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  margin-bottom: 12px;
}

.team-badge.opponent {
  background: var(--background-color3);
  color: var(--text-color1);
  border: 1px solid var(--border-color-hover1);
}

.team-logo {
  width: 72px;
  height: 72px;
  margin: 0 auto 12px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid var(--border-color-hover1);
  background: var(--background-color1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.team-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.logo-placeholder {
  font-size: 1.75rem;
  color: var(--border-color-hover);
}

.team-card h3 {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 10px;
  color: var(--text-color1);
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(14px, 2vw, 24px);
  width: 100%;
}

.profile-card {
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover1);
  border-radius: 14px;
  padding: 16px;
}

.profile-card h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0 0 12px;
  color: var(--text-color1);
}

.profile-card h3 i {
  color: var(--border-color-hover);
}

.profile-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px dashed var(--border-color-hover1);
  font-size: 0.88rem;
}

.profile-row:last-child {
  border-bottom: none;
}

.profile-label {
  color: var(--text-color1);
  opacity: 0.7;
  font-weight: 500;
}

.profile-value {
  font-weight: 600;
  text-align: right;
}

.profile-value.link {
  color: var(--border-color-hover);
  text-decoration: none;
}

.profile-value.link:hover {
  text-decoration: underline;
}

.profile-muted {
  margin: 0;
  font-size: 0.88rem;
  opacity: 0.65;
}

.profile-bio {
  margin: 16px 0 0;
  padding: 14px;
  border-radius: 12px;
  border: 1px dashed var(--border-color-hover1);
  background: var(--background-color2);
  font-size: 0.9rem;
  line-height: 1.55;
  opacity: 0.9;
}

.team-meta,
.team-ground {
  margin: 0;
  font-size: 0.88rem;
  opacity: 0.85;
  line-height: 1.45;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.team-ground {
  margin-top: 8px;
  font-size: 0.82rem;
  opacity: 0.75;
}

.vs-pill {
  align-self: center;
  font-weight: 800;
  font-size: 0.85rem;
  letter-spacing: 0.06em;
  padding: 10px 14px;
  border-radius: 12px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  box-shadow: 0 4px 15px var(--box-shadow);
}

/* Form */
.match-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-section {
  padding: 18px 0;
  border-bottom: 1px solid var(--border-color-hover1);
}

.form-section:last-of-type {
  border-bottom: none;
}

.form-section-title {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--border-color-hover);
  margin: 0 0 14px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
}

.field:last-child {
  margin-bottom: 0;
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.field label,
.field-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-color1);
}

.req {
  color: #ef4444;
}

.optional {
  font-weight: 400;
  opacity: 0.65;
  font-size: 0.85rem;
}

input,
select,
textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border-color-hover1);
  border-radius: 12px;
  background: var(--background-color2);
  color: var(--text-color1);
  font-family: inherit;
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px var(--box-shadow);
}

textarea {
  resize: vertical;
  min-height: 96px;
}

.field-error {
  font-size: 0.8rem;
  color: #ef4444;
  font-weight: 500;
}

/* Venue chips — resale-chip pattern */
.venue-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.venue-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-radius: 999px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  color: var(--text-color1);
  background: var(--background-color2);
  border: 2px solid var(--border-color-hover1);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.venue-chip input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.venue-chip i {
  color: var(--border-color-hover);
  font-size: 0.95rem;
}

.venue-chip:hover {
  transform: translateY(-2px);
  border-color: var(--border-color-hover);
  box-shadow: 0 6px 16px var(--box-shadow);
}

.venue-chip.selected {
  border-color: var(--border-color-hover);
  background: var(--background-color1);
  box-shadow: 0 4px 14px var(--box-shadow);
}

.venue-chip--disabled {
  opacity: 0.55;
  cursor: not-allowed;
  pointer-events: none;
}

.venue-chip--disabled.selected {
  opacity: 1;
  cursor: default;
}

.venue-locked-hint {
  margin: 10px 0 0;
  font-size: 0.82rem;
  color: var(--text-color2);
  line-height: 1.45;
}

.neutral-picker-wrap {
  margin-top: 16px;
  padding: 16px;
  border-radius: 14px;
  border: 1px dashed var(--border-color-hover1);
  background: var(--background-color2);
}

.officiating-section {
  border-top: 1px dashed var(--border-color-hover1);
  padding-top: 4px;
}

.form-section-lead {
  margin: -4px 0 16px;
  font-size: 0.88rem;
  color: var(--text-color2);
  line-height: 1.5;
}

.form-section-title i {
  margin-right: 8px;
  color: var(--border-color-hover);
}

.officiating-format-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 14px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 0.82rem;
  color: var(--text-color2);
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover1);
}

.officiating-format-hint i {
  color: var(--border-color-hover);
  margin-top: 2px;
}

.officiating-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.officiating-toggle__option {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-radius: 14px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  color: var(--text-color1);
  background: var(--background-color2);
  border: 2px solid var(--border-color-hover1);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
  flex: 1;
  min-width: 160px;
  justify-content: center;
}

.officiating-toggle__option input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.officiating-toggle__option i {
  color: var(--border-color-hover);
  font-size: 1rem;
}

.officiating-toggle__option:hover {
  transform: translateY(-2px);
  border-color: var(--border-color-hover);
  box-shadow: 0 6px 16px var(--box-shadow);
}

.officiating-toggle__option.selected {
  border-color: var(--border-color-hover);
  background: var(--background-color1);
  box-shadow: 0 4px 14px var(--box-shadow);
}

.officiating-details {
  margin-top: 18px;
}

.officiating-count-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}

.officiating-count-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px 12px;
  border-radius: 16px;
  cursor: pointer;
  text-align: center;
  background: var(--background-color2);
  border: 2px solid var(--border-color-hover1);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.officiating-count-card input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.officiating-count-card:hover {
  transform: translateY(-3px);
  border-color: var(--border-color-hover);
  box-shadow: 0 8px 20px var(--box-shadow);
}

.officiating-count-card.selected {
  border-color: var(--border-color-hover);
  background: var(--background-color1);
  box-shadow: 0 6px 18px var(--box-shadow);
}

.officiating-count-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color1);
  border: 1px solid var(--border-color-hover1);
  color: var(--border-color-hover);
  font-size: 1.1rem;
}

.officiating-count-card__label {
  font-weight: 700;
  font-size: 0.92rem;
}

.officiating-count-card__summary {
  font-size: 0.78rem;
  color: var(--text-color2);
  line-height: 1.35;
}

.officiating-count-card__fee {
  margin-top: 4px;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--border-color-hover);
}

.officiating-slots-panel {
  margin-top: 18px;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid var(--border-color-hover1);
  background: linear-gradient(
    180deg,
    var(--background-color1) 0%,
    var(--background-color2) 100%
  );
}

.officiating-slots-panel__head h4 {
  margin: 0 0 4px;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.officiating-slots-panel__head h4 i {
  color: var(--border-color-hover);
}

.officiating-slots-panel__head p {
  margin: 0 0 14px;
  font-size: 0.82rem;
  color: var(--text-color2);
}

.officiating-slot-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.officiating-slot-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--background-color1);
  border: 1px solid var(--border-color-hover1);
  animation: slot-in 0.35s ease both;
}

.officiating-slot-row:nth-child(2) { animation-delay: 0.05s; }
.officiating-slot-row:nth-child(3) { animation-delay: 0.1s; }

@keyframes slot-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.officiating-slot-row__icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color2);
  color: var(--border-color-hover);
  font-size: 1rem;
}

.officiating-slot-row__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.officiating-slot-row__body strong {
  font-size: 0.88rem;
}

.officiating-slot-row__body span {
  font-size: 0.8rem;
  color: var(--text-color2);
}

.officiating-slot-row__fee {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--border-color-hover);
  white-space: nowrap;
}

.officiating-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  font-size: 0.88rem;
}

.officiating-total i {
  margin-right: 6px;
}

.officiating-total strong {
  font-size: 1.05rem;
}

.officiating-reveal-enter-active,
.officiating-reveal-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.officiating-reveal-enter-from,
.officiating-reveal-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Buttons — MainEntry .btn */
.form-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  margin-top: 8px;
  border-top: 1px dashed var(--border-color-hover1);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 12px clamp(18px, 4vw, 28px);
  border-radius: 32px;
  cursor: pointer;
  font-weight: 600;
  font-size: clamp(0.85rem, 2vw, 0.95rem);
  font-family: inherit;
  border: 1px solid var(--border-color-hover1);
  background: var(--background-color2);
  color: var(--text-color1);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.btn.primary {
  background: var(--border-color-hover);
  border-color: var(--border-color-hover);
  color: var(--text-color3);
  box-shadow: 0 4px 15px var(--box-shadow);
}

.btn.primary:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 6px 18px var(--box-shadow);
}

.btn.primary:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn.outline:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px var(--box-shadow);
}

/* State cards */
.state-card {
  width: 100%;
  box-sizing: border-box;
  text-align: center;
  padding: 56px clamp(20px, 4vw, 48px);
  border-radius: 18px;
  border: 1px solid var(--border-color-hover1);
  background: var(--background-color1);
  box-shadow: 0 4px 18px var(--box-shadow);
}

.state-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  background: var(--background-color3);
  color: var(--border-color-hover);
}

.state-icon.error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.state-card h3 {
  margin: 0 0 8px;
  font-size: 1.2rem;
  font-weight: 700;
}

.state-card p {
  margin: 0 0 20px;
  opacity: 0.8;
  line-height: 1.5;
}

.loading-card .state-icon {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: grid;
  place-items: center;
  z-index: 1000;
  padding: 20px;
}

.modal-card {
  background: var(--background-color1);
  border: 1px solid var(--border-color-hover1);
  border-radius: 20px;
  padding: 28px 26px;
  max-width: 480px;
  width: 100%;
  text-align: center;
  box-shadow: 0 16px 48px var(--box-shadow);
  border-top: 4px solid var(--border-color-hover);
}

.modal-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 14px;
  border-radius: 14px;
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--border-color-hover);
}

.modal-card h3 {
  margin: 0 0 10px;
  font-size: 1.25rem;
  font-weight: 700;
}

.modal-lead {
  margin: 0 0 16px;
  font-size: 0.92rem;
  color: var(--text-color2);
  line-height: 1.5;
}

.modal-officiating-note {
  margin: 0 0 20px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid var(--border-color-hover1);
  background: var(--background-color2);
  text-align: left;
}

.modal-officiating-note__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--border-color-hover);
}

.modal-officiating-note__title i {
  font-size: 0.95rem;
}

.modal-officiating-note p {
  margin: 0 0 8px;
  font-size: 0.84rem;
  color: var(--text-color2);
  line-height: 1.5;
}

.modal-officiating-note p:last-child {
  margin-bottom: 0;
}

.modal-officiating-note__fee {
  font-size: 0.82rem;
}

.modal-officiating-note__fee strong {
  color: var(--text-color1);
  font-weight: 700;
}

.modal-officiating-note--active {
  border-color: var(--border-color-hover);
}

.modal-officiating-slots {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modal-officiating-slots li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.84rem;
  color: var(--text-color1);
}

.modal-officiating-slots li i {
  width: 18px;
  color: var(--border-color-hover);
  text-align: center;
}

.modal-officiating-slots li em {
  margin-left: auto;
  font-style: normal;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--border-color-hover);
}

.modal-officiating-note__split {
  margin: 10px 0 0;
  padding-top: 10px;
  border-top: 1px dashed var(--border-color-hover1);
  font-size: 0.82rem;
  color: var(--text-color2);
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.modal-officiating-note__split i {
  color: var(--border-color-hover);
  margin-top: 2px;
}

.modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 992px) {
  .profile-grid {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }
}

@media (max-width: 768px) {
  .challenge-team-page {
    padding-top: 76px;
    padding-left: 14px;
    padding-right: 14px;
  }

  .page-hero {
    padding: 24px 18px;
  }

  .page-hero-inner {
    text-align: center;
  }

  .page-title {
    justify-content: center;
  }

  .teams-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .vs-pill {
    justify-self: center;
  }

  .field-row {
    grid-template-columns: 1fr;
  }

  .venue-chips {
    flex-direction: column;
  }

  .venue-chip {
    justify-content: center;
  }

  .officiating-toggle {
    flex-direction: column;
  }

  .officiating-toggle__option {
    width: 100%;
  }

  .officiating-count-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .form-actions .btn {
    width: 100%;
  }

  .modal-actions {
    flex-direction: column-reverse;
  }

  .modal-actions .btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .panel {
    padding: 18px 16px;
  }
}
</style>
